<script setup lang="ts">
import { toMoney } from "@chatfood/core-utils";
import {
  AtomButton,
  AtomText,
  useImage,
  AtomIcon,
} from "@chatfood/design-system";
import { computed } from "vue";
import { useModalHubStore } from "../modal-hub";
import { css } from "./css";
import { useLocale } from "~/composables/use-locale";
import {
  useBusinessStore,
  useBrandingStore,
  useLoyaltyProgramStore,
  useFeatureMatrixStore,
} from "#imports";

const { t } = useLocale();

const business = useBusinessStore();
const brandingStore = useBrandingStore();
const loyaltyProgram = useLoyaltyProgramStore();
const featureMatrixStore = useFeatureMatrixStore();
const modalHub = useModalHubStore();

const { optimize } = useImage();

const coverImage = computed(() =>
  brandingStore.getMediaUrl("loyalty_marketing_banner"),
);
</script>

<template>
  <div :class="css()">
    <img
      v-if="coverImage"
      :class="css('cover')"
      :src="
        optimize(coverImage, {
          w: 640,
        })
      "
    />
    <div :class="css('wrapper')">
      <div :class="css('header', 'desktop')">
        <AtomText
          type="h1"
          color="color-base-0"
          :class="css('title-logged-out-desktop')"
        >
          {{ t("components.loyalty_program_guest.title") }}
        </AtomText>
        <AtomText
          v-if="loyaltyProgram.signupPoints > 0"
          type="h1"
          color="color-primary"
          data-test="highlighted-signup-bonus-desktop"
        >
          {{
            t("components.loyalty_program_guest.highlighted_bonus", {
              amount: toMoney(loyaltyProgram.signupPoints, business.currency),
            })
          }}
        </AtomText>
      </div>
      <div :class="css('header', 'mobile')">
        <AtomText
          type="h2"
          color="color-base-0"
          :class="css('title-logged-out-mobile')"
        >
          {{ t("components.loyalty_program_guest.title") }}
        </AtomText>
        <div v-if="loyaltyProgram.signupPoints > 0" :class="css('bonus')">
          <AtomText
            type="h2"
            color="color-primary"
            data-test="highlighted-signup-bonus-mobile"
          >
            {{
              t("components.loyalty_program_guest.highlighted_bonus", {
                amount: toMoney(loyaltyProgram.signupPoints, business.currency),
              })
            }}
          </AtomText>
          <AtomText
            tag="p"
            color="color-text-secondary"
            :class="css('welcome-bonus-mobile')"
            data-test="signup-bonus-mobile"
          >
            {{
              t("components.loyalty_program_guest.signup_text", {
                amount: toMoney(loyaltyProgram.signupPoints, business.currency),
              })
            }}
          </AtomText>
        </div>
      </div>
      <div>
        <AtomText type="body-bold" tag="h3" color="color-text-body">
          {{ t("components.loyalty_program_guest.subtitle") }}
        </AtomText>
        <div :class="css('items-content')">
          <div :class="css('item-box')">
            <div :class="css('icon-box')">
              <AtomIcon color="color-on-primary" name="crown" />
            </div>
            <div :class="css('benefit-info')">
              <AtomText type="body-bold" tag="p" color="color-text-body">
                {{ t("components.loyalty_program_guest.cashback_title") }}
              </AtomText>
              <AtomText type="body" tag="p" color="color-text-secondary">
                {{
                  t("components.loyalty_program_guest.cashback_text", {
                    amount: loyaltyProgram.orderAmountPercentageWorth,
                  })
                }}
              </AtomText>
            </div>
          </div>
          <div :class="css('item-box')">
            <div :class="css('icon-box')">
              <AtomIcon color="color-on-primary" name="gift-box" />
            </div>
            <div :class="css('benefit-info')">
              <AtomText type="body-bold" tag="p" color="color-text-body">
                {{ t("components.loyalty_program_guest.rewards_title") }}
              </AtomText>
              <AtomText type="body" tag="p" color="color-text-secondary">
                {{ t("components.loyalty_program_guest.rewards_text") }}
              </AtomText>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!featureMatrixStore.skipAuthFlow"
        :class="css('footer')"
        data-test="signup-footer"
      >
        <AtomButton
          wide
          variant="primary"
          theme="filled"
          type="button"
          size="large"
          @click="() => modalHub.showAuthManager()"
        >
          {{ t("components.loyalty_program_guest.signup_cta") }}
        </AtomButton>
        <AtomText
          v-if="loyaltyProgram.signupPoints > 0"
          type="small"
          tag="p"
          color="color-text-secondary"
          :class="css('welcome-bonus')"
          data-test="signup-bonus-desktop"
        >
          {{
            t("components.loyalty_program_guest.signup_text", {
              amount: toMoney(loyaltyProgram.signupPoints, business.currency),
            })
          }}
        </AtomText>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-program-guest {
  &__wrapper {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-lg);
    padding-bottom: 0;
    gap: var(--spacing-xl);

    @media (width >= $breakpoint-sm) {
      gap: var(--spacing-3xl);
      padding-bottom: var(--spacing-lg);
    }
  }

  &__cover {
    border-radius: var(--radii-card-md) var(--radii-card-md) 0 0;
    max-width: 100%;
    display: block;
    aspect-ratio: 16 / 5;

    @media (width <= $breakpoint-sm) {
      border-radius: 0;
    }
  }

  &__welcome-bonus,
  &__header--desktop {
    display: none;

    @media (width >= $breakpoint-sm) {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);
    }
  }

  &__header--mobile {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);

    @media (width >= $breakpoint-sm) {
      display: none;
    }
  }

  &__bonus {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  &__benefit-info {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  &__items-content {
    display: flex;
    gap: var(--spacing-md);
    margin-top: var(--spacing-md);

    @media (width <= $breakpoint-sm) {
      flex-direction: column;
    }
  }

  &__item-box {
    border: solid 1px var(--color-base-90);
    padding: var(--spacing-md);
    border-radius: var(--radii-card-sm);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    flex: 1;
  }

  &__icon-box {
    background-color: var(--color-primary);
    display: flex;
    width: 40px;
    height: 40px;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    margin-bottom: var(--spacing-sm);
  }

  &__footer {
    width: 100%;
    position: sticky;
    bottom: 0;
    padding-block: var(--spacing-md);
    background: var(--color-background-shell);

    @media (width >= $breakpoint-sm) {
      width: 200px;
      position: initial;
      bottom: initial;
      padding-block: initial;
      background: initial;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      text-align: center;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-sm);
    }
  }
}
</style>
